import type { Coding, Stage, VitalLevel, VitalLimit } from '../types';

// Mock clinical code catalogue (LOINC + UCUM, SNOMED CT, ICD-10). Users only ever see display names.
const loinc = (code: string, display: string): Coding => ({ system: 'http://loinc.org', code, display });
const snomed = (code: string, display: string): Coding => ({ system: 'http://snomed.info/sct', code, display });
export const icd10 = (code: string, display: string): Coding => ({ system: 'http://hl7.org/fhir/sid/icd-10', code, display });

export interface VitalDef {
  key: string;
  stage: Stage;
  code: Coding;
  ucum: string;
  unit: string;
  group?: UnitGroup;
  choices?: { value: string; level: VitalLevel }[];
}

export type UnitGroup = 'temp' | 'mass' | 'length';
export interface UnitDef { id: string; label: string; toStd: (v: number) => number; fromStd: (v: number) => number }
const same = (v: number) => v;

// Values are always stored in the first (standard UCUM) unit of each group.
export const UNITS: Record<UnitGroup, UnitDef[]> = {
  temp: [{ id: 'Cel', label: '°C', toStd: same, fromStd: same }, { id: '[degF]', label: '°F', toStd: (v) => ((v - 32) * 5) / 9, fromStd: (v) => (v * 9) / 5 + 32 }],
  mass: [{ id: 'kg', label: 'kg', toStd: same, fromStd: same }, { id: 'g', label: 'g', toStd: (v) => v / 1000, fromStd: (v) => v * 1000 }],
  length: [{ id: 'cm', label: 'cm', toStd: same, fromStd: same }, { id: '[in_i]', label: 'in', toStd: (v) => v * 2.54, fromStd: (v) => v / 2.54 }],
};
export type DefaultUnits = Record<UnitGroup, string>;
export const STD_UNITS: DefaultUnits = { temp: 'Cel', mass: 'kg', length: 'cm' };

const round = (v: number, dp: number) => Math.round(v * 10 ** dp) / 10 ** dp;
export function unitFor(def: VitalDef, units: DefaultUnits, override?: string): UnitDef {
  if (!def.group) return { id: def.ucum, label: def.unit, toStd: same, fromStd: same };
  const list = UNITS[def.group];
  return list.find((u) => u.id === (override ?? units[def.group!])) ?? list[0];
}
export const toDisplay = (std: number, u: UnitDef) => String(round(u.fromStd(std), u.id === 'g' ? 0 : u.id === '[in_i]' ? 1 : 2));
export const toStd = (v: number, u: UnitDef) => round(u.toStd(v), 3);

// Sanity ranges in standard units; separate from the doctor's amber/red limits.
export const PLAUSIBLE: Record<string, [number, number]> = {
  bWeight: [0.5, 20], bLength: [30, 110], bHead: [25, 55], bTemp: [30, 43], bPulse: [40, 250],
  weight: [30, 200], bpSys: [60, 250], bpDia: [30, 150], temp: [30, 43], pulse: [40, 250],
  respRate: [4, 70], spo2: [50, 100], bRespRate: [10, 120], bSpo2: [50, 100],
};
export const isImplausible = (key: string, raw: string) => {
  const r = PLAUSIBLE[key];
  const v = Number(raw);
  return !!r && raw !== '' && !Number.isNaN(v) && (v < r[0] || v > r[1]);
};

const num = (key: string, stage: Stage, code: Coding, ucum: string, unit = ucum, group?: UnitGroup): VitalDef => ({ key, stage, code, ucum, unit, group });
const levels = (vals: string[], lv: VitalLevel[]) => vals.map((value, i) => ({ value, level: lv[i] }));

export const VITALS: VitalDef[] = [
  num('bpSys', 'pregnancy', loinc('8480-6', 'Systolic blood pressure'), 'mm[Hg]', 'mmHg'),
  num('bpDia', 'pregnancy', loinc('8462-4', 'Diastolic blood pressure'), 'mm[Hg]', 'mmHg'),
  num('weight', 'pregnancy', loinc('29463-7', 'Body weight'), 'kg', 'kg', 'mass'),
  num('pulse', 'pregnancy', loinc('8867-4', 'Heart rate'), '/min'),
  num('respRate', 'pregnancy', loinc('9279-1', 'Respiratory rate'), '/min'),
  num('spo2', 'pregnancy', loinc('59408-5', 'Oxygen saturation by pulse oximetry'), '%'),
  num('temp', 'pregnancy', loinc('8310-5', 'Body temperature'), 'Cel', '°C', 'temp'),
  num('hb', 'pregnancy', loinc('718-7', 'Hemoglobin'), 'g/dL'),
  { key: 'urineProtein', stage: 'pregnancy', code: loinc('20454-5', 'Protein in urine'), ucum: '', unit: '', choices: levels(['nil', 'trace', '1+', '2+', '3+'], ['normal', 'normal', 'amber', 'red', 'red']) },
  { key: 'urineSugar', stage: 'pregnancy', code: loinc('25428-4', 'Glucose in urine'), ucum: '', unit: '', choices: levels(['nil', 'trace', '1+', '2+'], ['normal', 'normal', 'amber', 'red']) },
  { key: 'oedema', stage: 'pregnancy', code: loinc('44966-2', 'Oedema'), ucum: '', unit: '', choices: levels(['none', 'mild', 'marked'], ['normal', 'amber', 'red']) },
  num('fundalHeight', 'pregnancy', loinc('11881-0', 'Fundal height'), 'cm'),
  num('fhr', 'pregnancy', loinc('55283-6', 'Fetal heart rate'), '/min'),
  num('bWeight', 'baby', loinc('29463-7', 'Body weight'), 'kg', 'kg', 'mass'),
  num('bLength', 'baby', loinc('8306-3', 'Body length'), 'cm', 'cm', 'length'),
  num('bHead', 'baby', loinc('9843-4', 'Head circumference'), 'cm', 'cm', 'length'),
  num('bTemp', 'baby', loinc('8310-5', 'Body temperature'), 'Cel', '°C', 'temp'),
  num('bPulse', 'baby', loinc('8867-4', 'Heart rate'), '/min'),
  num('bRespRate', 'baby', loinc('9279-1', 'Respiratory rate'), '/min'),
  num('bSpo2', 'baby', loinc('59408-5', 'Oxygen saturation by pulse oximetry'), '%'),
];

export const DEFAULT_LIMITS: Record<string, VitalLimit> = {
  bpSys: { redLow: 80, amberLow: 90, amberHigh: 140, redHigh: 160 },
  bpDia: { redLow: 50, amberLow: 60, amberHigh: 90, redHigh: 110 },
  pulse: { redLow: 50, amberLow: 60, amberHigh: 100, redHigh: 120 },
  temp: { redLow: 35, amberLow: 36, amberHigh: 37.5, redHigh: 38.5 },
  hb: { redLow: 7, amberLow: 11 },
  fhr: { redLow: 100, amberLow: 110, amberHigh: 160, redHigh: 180 },
  bTemp: { redLow: 35.5, amberLow: 36.5, amberHigh: 37.5, redHigh: 38.5 },
  bPulse: { redLow: 80, amberLow: 100, amberHigh: 160, redHigh: 180 },
  // Standard library additions (starting values; a doctor reviews before any clinic uses them).
  spo2: { redLow: 92, amberLow: 95 },
  respRate: { redLow: 8, amberLow: 12, amberHigh: 20, redHigh: 24 },
  glucoseFasting: { redLow: 54, amberLow: 70, amberHigh: 100, redHigh: 126 },
  glucoseRandom: { redLow: 54, amberLow: 70, amberHigh: 140, redHigh: 200 },
  bmi: { redLow: 16, amberLow: 18.5, amberHigh: 23, redHigh: 27.5 }, // Asian (WHO/ICMR) cut-offs
  painScore: { amberHigh: 3, redHigh: 6 },
  bRespRate: { redLow: 20, amberLow: 30, amberHigh: 50, redHigh: 60 }, // 60+ = fast breathing danger sign (IMNCI)
  bSpo2: { redLow: 90, amberLow: 95 },
};

// Standard vitals library: every vital the platform can hold traffic-light ranges for, coded (LOINC).
// Care programmes draw from it (pregnancy = adult + pregnancy vitals; baby = baby vitals; IVF and
// weight loss later = adult). Vitals not yet used by a programme simply wait here, ready.
export type VitalGroup = 'adult' | 'pregnancy' | 'baby';
export interface LibraryVital { key: string; group: VitalGroup; unit: string; code: Coding }
export const VITAL_LIBRARY: LibraryVital[] = [
  { key: 'bpSys', group: 'adult', unit: 'mmHg', code: loinc('8480-6', 'Systolic blood pressure') },
  { key: 'bpDia', group: 'adult', unit: 'mmHg', code: loinc('8462-4', 'Diastolic blood pressure') },
  { key: 'pulse', group: 'adult', unit: '/min', code: loinc('8867-4', 'Heart rate') },
  { key: 'spo2', group: 'adult', unit: '%', code: loinc('59408-5', 'Oxygen saturation by pulse oximetry') },
  { key: 'respRate', group: 'adult', unit: '/min', code: loinc('9279-1', 'Respiratory rate') },
  { key: 'temp', group: 'adult', unit: '°C', code: loinc('8310-5', 'Body temperature') },
  { key: 'glucoseFasting', group: 'adult', unit: 'mg/dL', code: loinc('1558-6', 'Fasting glucose') },
  { key: 'glucoseRandom', group: 'adult', unit: 'mg/dL', code: loinc('2345-7', 'Glucose') },
  { key: 'bmi', group: 'adult', unit: 'kg/m²', code: loinc('39156-5', 'Body mass index') },
  { key: 'painScore', group: 'adult', unit: '0–10', code: loinc('72514-3', 'Pain severity 0-10') },
  { key: 'hb', group: 'pregnancy', unit: 'g/dL', code: loinc('718-7', 'Hemoglobin') },
  { key: 'fhr', group: 'pregnancy', unit: '/min', code: loinc('55283-6', 'Fetal heart rate') },
  { key: 'bTemp', group: 'baby', unit: '°C', code: loinc('8310-5', 'Body temperature') },
  { key: 'bPulse', group: 'baby', unit: '/min', code: loinc('8867-4', 'Heart rate') },
  { key: 'bRespRate', group: 'baby', unit: '/min', code: loinc('9279-1', 'Respiratory rate') },
  { key: 'bSpo2', group: 'baby', unit: '%', code: loinc('59408-5', 'Oxygen saturation by pulse oximetry') },
];

export function interpret(def: VitalDef, raw: string, limit?: VitalLimit): VitalLevel | undefined {
  if (!raw) return undefined;
  if (def.choices) return def.choices.find((c) => c.value === raw)?.level;
  const v = Number(raw);
  if (Number.isNaN(v) || !limit) return 'normal';
  if ((limit.redLow !== undefined && v < limit.redLow) || (limit.redHigh !== undefined && v > limit.redHigh)) return 'red';
  if ((limit.amberLow !== undefined && v < limit.amberLow) || (limit.amberHigh !== undefined && v > limit.amberHigh)) return 'amber';
  return 'normal';
}

export const SYMPTOMS: { key: string; stage: Stage; code: Coding }[] = [
  { key: 'headache', stage: 'pregnancy', code: snomed('25064002', 'Headache') },
  { key: 'blurredVision', stage: 'pregnancy', code: snomed('111516008', 'Blurred vision') },
  { key: 'swelling', stage: 'pregnancy', code: snomed('267038008', 'Oedema') },
  { key: 'bleeding', stage: 'pregnancy', code: snomed('289530006', 'Vaginal bleeding') },
  { key: 'reducedMovement', stage: 'pregnancy', code: snomed('276369006', 'Reduced fetal movement') },
  { key: 'abdominalPain', stage: 'pregnancy', code: snomed('21522001', 'Abdominal pain') },
  { key: 'vomiting', stage: 'pregnancy', code: snomed('422400008', 'Vomiting') },
  { key: 'fever', stage: 'baby', code: snomed('386661006', 'Fever') },
  { key: 'poorFeeding', stage: 'baby', code: snomed('78164000', 'Feeding problem') },
  { key: 'bVomiting', stage: 'baby', code: snomed('422400008', 'Vomiting') },
  { key: 'looseStools', stage: 'baby', code: snomed('398032003', 'Loose stool') },
  { key: 'cough', stage: 'baby', code: snomed('49727002', 'Cough') },
  { key: 'rash', stage: 'baby', code: snomed('271807003', 'Rash') },
  { key: 'injectionSwelling', stage: 'baby', code: snomed('95376002', 'Injection site swelling') },
  // WHO young-infant danger signs (used by the baby danger-sign check).
  { key: 'convulsions', stage: 'baby', code: snomed('91175000', 'Seizure') },
  { key: 'babyLethargic', stage: 'baby', code: snomed('214264003', 'Lethargy') },
  { key: 'chestIndrawing', stage: 'baby', code: icd10('R06.8', 'Chest indrawing') },
];

export const VACCINE_CODES: Record<string, Coding> = {
  'ps-birth': snomed('1119305005', 'BCG, OPV-0, Hep B (birth)'),
  'ps-6w': snomed('1119349007', 'Penta-1, OPV-1, Rota-1, PCV-1'),
  'ps-10w': snomed('1119350007', 'Penta-2, OPV-2, Rota-2'),
  'ps-14w': snomed('1119351006', 'Penta-3, OPV-3, Rota-3, PCV-2'),
  'ps-9m': snomed('1119352004', 'MR-1, PCV booster'),
};

export const ANSWER_CODE = (id: string, display: string): Coding => snomed(`mock-${id}`, display);
export const UNCODED = (display: string): Coding => icd10('', display);
